import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl } from '../../../utils/mediaUtils';
import { 
  Users, 
  Upload, 
  Clock, 
  Save,
  Eye,
  X
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function ParentsHeroForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Parents',
    title: 'Parents',
    summary: 'Strengthening the partnership between families and school through meaningful collaboration.',
    slides: [
      { image: '', order: 1, caption: 'School Community' },
      { image: '', order: 2, caption: 'Parent Engagement' },
      { image: '', order: 3, caption: 'Collaborative Growth' }
    ]
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    summary: false
  });
  const [pendingSlides, setPendingSlides] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});

  const getAuthHeaders = () => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    const init = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (!res.ok) return;
      const json = await res.json();
      if (json.ok && Array.isArray(json.data)) {
        const map = {};
        json.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('[ParentsHeroForm] Media fetch error:', err.message);
    }
  };

  const resolveImage = (mediaId, index) => {
    if (index !== undefined && brokenImages[index]) {
      return null;
    }
    if (!mediaId) return null;
    if (typeof mediaId === 'object' && mediaId !== null) {
      mediaId = mediaId.image || mediaId.url || mediaId.file_path || '';
    }
    const str = String(mediaId).trim();
    if (!str) return null;
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (mediaMap[str]) {
      return mediaMap[str];
    }
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) {
      return mediaMap[num];
    }
    const resolved = getImageUrl(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return null;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=parents`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'parents-hero' || s.section_type === 'hero');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=parents`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'parents-hero' || s.section_type === 'hero');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        let rawSlides = Array.isArray(en.slides) ? en.slides : (Array.isArray(en.hero_slides) ? en.hero_slides : []);

        const paddedSlides = [0, 1, 2].map((idx) => {
          const raw = rawSlides[idx];
          const defaultCaption = idx === 0 ? 'School Community' : (idx === 1 ? 'Parent Engagement' : 'Collaborative Growth');
          if (raw) {
            if (typeof raw === 'object' && raw !== null) {
              return {
                image: raw.image || raw.url || raw.file_path || '',
                order: idx + 1,
                caption: raw.caption || defaultCaption
              };
            }
            return { image: String(raw), order: idx + 1, caption: defaultCaption };
          }
          return { image: '', order: idx + 1, caption: defaultCaption };
        });

        const loadedData = {
          kicker: en.kicker || 'Parents',
          title: en.title || 'Parents',
          summary: en.summary || en.subtitle || 'Strengthening the partnership between families and school through meaningful collaboration.',
          slides: paddedSlides
        };

        setFormData(loadedData);
        setOriginalData(loadedData);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, summary: false };
              const pSlides = {};

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  if (['kicker', 'title', 'summary'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                  } else if (c.field_id?.includes('slides.')) {
                    const m = c.field_id.match(/slides\.(\d+)/);
                    if (m) pSlides[`slide_${m[1]}`] = true;
                  }
                }
              });

              setPendingFields(pFields);
              setPendingSlides(pSlides);
            }
          }
        } catch (pe) {
          console.warn('[ParentsHeroForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[ParentsHeroForm] Load error:', err);
      toast.error('Failed to load Parents Hero content');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlideChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.slides];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, slides: next };
    });
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran foto maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format foto harus JPG, PNG, atau WebP');
      return;
    }

    setImageUploading(prev => ({ ...prev, [index]: true }));
    setUploadProgress(prev => ({ ...prev, [index]: 10 }));

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (err) {}
    }
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setUploadProgress(prev => ({ ...prev, [index]: pct }));
      }
    };

    xhr.onload = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          let mediaUrl = data.url || data.file_path || data.path || res.url;
          if (mediaUrl && !mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            mediaUrl = `${origin}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;
          }
          if (mediaUrl) {
            handleSlideChange(index, 'image', mediaUrl);
            setBrokenImages(prev => ({ ...prev, [index]: false }));
            toast.success(`Foto Slide ${index + 1} berhasil diunggah`);
          } else {
            toast.error('Gagal membaca URL foto');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleSubmitField = async (field) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    if (formData[field] === originalData[field]) {
      toast.warning('Tidak ada perubahan pada field ini');
      return;
    }

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: field.charAt(0).toUpperCase() + field.slice(1),
          old_value: originalData[field],
          new_value: formData[field],
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${field}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: formData[field] }));
        try {
          sessionStorage.removeItem('cms_page_parents');
          sessionStorage.removeItem('cms_sections_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ParentsHeroForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmitSlide = async (index) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const slide = formData.slides[index];
    const key = `slide_${index}`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `slides.${index}.update`,
          field_name: `Hero Slide ${index + 1}`,
          old_value: originalData.slides?.[index] || {},
          new_value: slide,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Hero Slide ${index + 1} berhasil diajukan untuk approval`);
        setPendingSlides(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.slides || [])];
          next[index] = slide;
          return { ...prev, slides: next };
        });
        try {
          sessionStorage.removeItem('cms_page_parents');
          sessionStorage.removeItem('cms_sections_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ParentsHeroForm] Submit slide error:', err);
      toast.error('Error submitting slide: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Parents Hero Section content...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Users size={22} />
        </div>
        <div>
          <h2 className="unified-title">Parents Hero Section</h2>
          <p className="unified-subtitle">
            Kelola judul utama, ringkasan pengantar, dan foto carousel slideshow pada header halaman Parents (Tepat 3 Slide Carousel)
          </p>
        </div>
      </div>

      {/* Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker}
            placeholder="e.g. Parents"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title}
            placeholder="e.g. Parents"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Summary */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Summary</label>
          {pendingFields.summary && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.summary}
            onChange={(e) => handleFieldChange('summary', e.target.value)}
            disabled={pendingFields.summary}
            placeholder="Strengthening the partnership between families and school through meaningful collaboration."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('summary')}
            disabled={pendingFields.summary || submitting.summary || formData.summary === originalData.summary}
          >
            <Save size={14} /> {submitting.summary ? 'Saving...' : (pendingFields.summary ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Hero Slideshow Management */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Hero Slideshow (Tepat 3 Slide - Hanya Ganti Foto)</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Foto-foto ini membentuk carousel slideshow hero otomatis di bagian paling atas halaman Parents (Maksimal 3 foto sesuai standar seluruh Hero Section).
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginTop: '16px' }}>
          {formData.slides.map((slide, idx) => {
            const isPending = pendingSlides[`slide_${idx}`];
            const isSubmitting = submitting[`slide_${idx}`];
            const isUploading = imageUploading[idx];
            const progress = uploadProgress[idx] || 0;
            const resolvedImg = resolveImage(slide.image, idx);

            return (
              <div 
                key={idx}
                style={{
                  border: isPending ? '1.5px dashed #f59e0b' : '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '16px',
                  background: isPending ? '#fffdf7' : '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ background: '#eff6ff', color: '#2563eb', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '12px' }}>
                    SLIDE #{idx + 1}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {isPending && (
                      <span className="unified-pending-pill">
                        <Clock size={11} /> Pending
                      </span>
                    )}
                  </div>
                </div>

                {/* Preview with Upload Overlay & Clickable Lightbox */}
                <div 
                  style={{
                    height: '150px',
                    borderRadius: '10px',
                    background: '#0f172a',
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #cbd5e1',
                    cursor: resolvedImg ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (resolvedImg && !isUploading) {
                      setLightboxImage(resolvedImg);
                    }
                  }}
                  title={resolvedImg ? 'Klik untuk melihat foto lebih besar' : ''}
                >
                  {resolvedImg ? (
                    <img 
                      src={resolvedImg} 
                      alt={`Slide ${idx + 1}`} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      onError={() => {
                        setBrokenImages(prev => ({ ...prev, [idx]: true }));
                      }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                      <Users size={28} style={{ margin: '0 auto 4px auto' }} />
                      <div style={{ fontSize: '11px' }}>Belum ada foto slide</div>
                    </div>
                  )}

                  {resolvedImg && !isUploading && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(0,0,0,0.6)',
                      borderRadius: '50%',
                      padding: '4px',
                      color: '#fff',
                      display: 'flex'
                    }}>
                      <Eye size={12} />
                    </div>
                  )}

                  {isUploading && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                      <div className="unified-loading-spinner" style={{ width: '20px', height: '20px', marginBottom: '6px' }} />
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>{progress}%</span>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <label 
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '8px',
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#2563eb',
                    cursor: isUploading ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Upload size={13} />
                  {isUploading ? 'Uploading...' : (slide.image ? 'Ganti Foto Slide' : 'Unggah Foto Slide')}
                  <input 
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    disabled={isUploading}
                    onChange={(e) => handleImageUpload(idx, e)}
                  />
                </label>

                {/* Caption Input */}
                <div>
                  <label className="unified-label" style={{ fontSize: '11px', marginBottom: '4px' }}>
                    Caption / Keterangan Slide
                  </label>
                  <input 
                    type="text"
                    className="unified-input"
                    value={slide.caption || ''}
                    disabled={isPending}
                    onChange={(e) => handleSlideChange(idx, 'caption', e.target.value)}
                    placeholder="e.g. School Community & Collaboration"
                    style={{ fontSize: '12px', padding: '6px 10px' }}
                  />
                </div>

                {/* Submit button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitSlide(idx)}
                    disabled={isPending || isSubmitting}
                    style={{ padding: '6px 14px', fontSize: '12px' }}
                  >
                    <Save size={13} />
                    {isSubmitting ? 'Saving...' : (isPending ? 'Pending' : 'Save Slide')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox Preview Modal */}
      {lightboxImage && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              <X size={28} />
            </button>
            <img 
              src={lightboxImage} 
              alt="Slide Preview Full" 
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '8px', objectFit: 'contain' }} 
            />
          </div>
        </div>
      )}
    </div>
  );
}
